import { useCallback, useEffect, useState } from 'react';
import Modal from './Modal';
import Button from './Button';
import { useToast } from './Toast';
import { createQualityCheck, listQualityCheckCandidates } from '../api/qualityCheck';
import { fromLocalInputValue, localTimeZoneLabel } from '../utils/localDateTime';
import './AddQualityCheckModal.css';

// "Add to Quality Check" — pick one of a project's workflows and register it as a
// scheduled regression check.
//
// Candidates come from the backend (it owns the template registry) rather than
// from the client's graph: the dashboard has no graph data at all, because
// list_projects' response_model drops workflowNodes.

const INTERVALS = [
  { value: 'manual', label: 'Manual only' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

// One candidate's label + metadata. Rendered inside a radio when there is a
// choice to make, and on its own when there isn't (see below).
function CandidateBody({ candidate: c }) {
  return (
    <span className="qc-candidate-body">
      <span className="qc-candidate-label">{c.label}</span>
      <span className="qc-candidate-meta">
        {c.tileCount} module{c.tileCount === 1 ? '' : 's'}
        {/* A derived workflow has no template to name — say how it
            will run instead, so "custom" isn't a silent difference. */}
        {c.templateKey ? ` · ${c.templateKey}` : c.derived ? ' · custom' : ''}
      </span>
      {/* The backend never guesses a template, so an unregisterable
          workflow always comes with a plain-language reason. A
          refusal is always a graph problem — an unwired input, a
          loop, a module with nothing saved — never cost. */}
      {!c.runnable && c.reason && <span className="qc-candidate-reason">{c.reason}</span>}
      {/* Cost is stated, not enforced: an expensive workflow is
          selectable and says what it will queue. */}
      {c.runnable && c.costNote && <span className="qc-candidate-note">{c.costNote}</span>}
    </span>
  );
}

export default function AddQualityCheckModal({ open, project, onClose, onCreated }) {
  const [candidates, setCandidates] = useState(null);
  const [error, setError] = useState(null);
  const [groupId, setGroupId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [interval, setInterval] = useState('weekly');
  const [startLocal, setStartLocal] = useState('');
  // 'state' (replay the frozen module settings) or 'interaction' (replay the
  // recorded session). Defaults to 'state' so the original behaviour is what a
  // user gets without choosing.
  const [runMode, setRunMode] = useState('state');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const projectId = project?.projectId;

  const load = useCallback(async () => {
    if (!projectId) return;
    setCandidates(null);
    setError(null);
    try {
      const found = await listQualityCheckCandidates(projectId);
      setCandidates(found);
      const first = found.find((c) => c.runnable);
      setGroupId(first ? first.groupId : '');
      setName('');
      setDescription('');
      setRunMode('state');
      setStartLocal('');
    } catch (err) {
      setError(err.message || 'Could not read this project&apos;s workflows');
    }
  }, [projectId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const selected = (candidates || []).find((c) => c.groupId === groupId) || null;
  // With exactly one registerable workflow there is no choice to make, so the
  // radio is busywork: it renders pre-checked and clicking it does nothing.
  // Anything the user can't pick still needs its refusal shown, so this only
  // collapses when the WHOLE list is that one candidate.
  const runnable = (candidates || []).filter((c) => c.runnable);
  const soleCandidate = runnable.length === 1 && candidates.length === 1 ? runnable[0] : null;

  const submit = async (e) => {
    e.preventDefault();
    if (!selected || busy) return;
    setBusy(true);
    try {
      const entry = await createQualityCheck({
        projectId,
        groupId: selected.groupId,
        name: name.trim() || undefined,
        description: description.trim() || undefined,
        interval,
        startAt: interval === 'manual' ? undefined : fromLocalInputValue(startLocal),
        runMode,
      });
      toast.success(`Added "${entry.name}" to Quality Check`);
      onCreated?.(entry);
      onClose?.();
    } catch (err) {
      toast.error(err.message || 'Could not add this workflow');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add to Quality Check" className="qc-add-modal">
      <form onSubmit={submit}>
        <p className="qc-add-intro">
          Re-runs this workflow on a schedule and compares the numbers it produces against a saved
          baseline, so an unexpected change shows up as a failed check.
        </p>

        {error && <p className="qc-add-error">{error}</p>}
        {!candidates && !error && <p className="qc-add-empty">Reading workflows…</p>}
        {candidates && !candidates.length && (
          <p className="qc-add-empty">This project has no workflows to check.</p>
        )}

        {soleCandidate && (
          <div className="qc-candidate qc-candidate-sole">
            <CandidateBody candidate={soleCandidate} />
          </div>
        )}

        {candidates && candidates.length > 0 && !soleCandidate && (
          <ul className="qc-candidate-list">
            {candidates.map((c) => (
              <li key={c.groupId}>
                <label className={`qc-candidate${c.runnable ? '' : ' qc-candidate-disabled'}`}>
                  <input
                    type="radio"
                    name="qc-group"
                    value={c.groupId}
                    checked={groupId === c.groupId}
                    disabled={!c.runnable}
                    onChange={() => setGroupId(c.groupId)}
                  />
                  <CandidateBody candidate={c} />
                </label>
              </li>
            ))}
          </ul>
        )}

        {selected && (
          <>
            <label className="qc-add-field">
              <span>Name</span>
              <input
                type="text"
                value={name}
                placeholder={selected.label}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="qc-add-field">
              <span>Description</span>
              <textarea
                rows={3}
                value={description}
                placeholder="What should this check catch?"
                onChange={(e) => setDescription(e.target.value)}
              />
            </label>
            <label className="qc-add-field">
              <span>Schedule</span>
              <select value={interval} onChange={(e) => setInterval(e.target.value)}>
                {INTERVALS.map((i) => (
                  <option key={i.value} value={i.value}>
                    {i.label}
                  </option>
                ))}
              </select>
            </label>
            {interval !== 'manual' && (
              <>
                {/* A calendar-event start: the picked weekday and minute are what
                    recur. Left blank, the backend keeps its overnight default. */}
                <label className="qc-add-field">
                  <span>Starts</span>
                  <input
                    type="datetime-local"
                    value={startLocal}
                    onChange={(e) => setStartLocal(e.target.value)}
                  />
                </label>
                <p className="qc-add-hint">
                  {startLocal
                    ? `Runs ${
                        interval === 'weekly' ? 'every week' : 'every month'
                      } from this date and time (${localTimeZoneLabel()}).`
                    : 'Leave blank to run overnight (UTC), so checks don’t compete with your own jobs for compute.'}
                </p>
              </>
            )}
            {/* WHAT gets replayed. Offered as a choice only when the recorded
                session can actually rebuild this workflow — the backend answers
                that (`interactionReplayable`), because a session recorded after the
                modules already existed cannot replay from an empty canvas. */}
            <label className="qc-add-field">
              <span>Replay</span>
              <select
                value={runMode}
                onChange={(e) => setRunMode(e.target.value)}
                disabled={!selected.interactionReplayable}
              >
                <option value="state">Module settings only</option>
                <option value="interaction" disabled={!selected.interactionReplayable}>
                  My recorded session
                  {selected.interactionPromptCount
                    ? ` (${selected.interactionPromptCount} prompt${
                        selected.interactionPromptCount === 1 ? '' : 's'
                      })`
                    : ''}
                </option>
              </select>
            </label>
            {runMode === 'interaction' ? (
              <p className="qc-add-hint qc-add-caveat">
                Replays your session in order — every prompt goes back to the assistant, which
                builds and wires the modules itself, and your manual changes are re-applied between
                them. Reports when the assistant answers a prompt differently than it did. Costs one
                assistant call per prompt, every run.
              </p>
            ) : (
              /* The settings are SNAPSHOTTED now, so say so — editing the project
                 later has no effect until the check is added again, and that is the
                 whole reason a drift result means the code changed, not the inputs. */
              <p className="qc-add-hint qc-add-caveat">
                Captures this workflow&apos;s current module settings and geometry, and re-runs
                those every time. Later edits to the project won&apos;t change this check until you
                add it again.
              </p>
            )}
            {!selected.interactionReplayable && selected.interactionReason && (
              <p className="qc-add-hint">{selected.interactionReason}</p>
            )}
            {selected.stepLabels?.length > 0 && (
              <p className="qc-add-hint">
                Runs {selected.stepLabels.length} step
                {selected.stepLabels.length === 1 ? '' : 's'}: {selected.stepLabels.join(' → ')}
              </p>
            )}
          </>
        )}

        {/* `.ui-modal__actions` (the Modal primitive's own class, as ConfirmDialog uses)
            rather than the legacy `.modal-actions`, which lives in Dashboard.css — a
            stylesheet this component does not import either — and which greys whichever
            button happens to be FIRST rather than the one that means "cancel". */}
        <div className="ui-modal__actions qc-add-actions">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={!selected || busy}>
            {busy ? 'Adding…' : 'Add check'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
